'use client';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/cn';

/** Radice del menu a tendina: gestisce apertura/chiusura. */
const DropdownMenu = DropdownMenuPrimitive.Root;

/** Elemento che apre il menu al click. */
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

/** Raggruppa logicamente più {@link DropdownMenuItem} (solo semantico, nessuno stile). */
const DropdownMenuGroup = DropdownMenuPrimitive.Group;

/** Portale che renderizza il contenuto del menu fuori dal flusso DOM del genitore. */
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

/** Contenitore per un sottomenu annidato: avvolge {@link DropdownMenuSubTrigger} e {@link DropdownMenuSubContent}. */
const DropdownMenuSub = DropdownMenuPrimitive.Sub;

/** Raggruppa {@link DropdownMenuRadioItem} in un unico gruppo a selezione singola. */
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

export interface DropdownMenuSubTriggerProps extends React.ComponentPropsWithoutRef<
	typeof DropdownMenuPrimitive.SubTrigger
> {
	/** Se true, aggiunge un rientro (per allinearsi alle voci con icona/checkbox). */
	inset?: boolean;
}

/** Voce che apre un sottomenu al passaggio del mouse/tastiera. */
const DropdownMenuSubTrigger = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
	DropdownMenuSubTriggerProps
>(({ className, inset, children, ...props }, ref) => (
	<DropdownMenuPrimitive.SubTrigger
		ref={ref}
		className={cn(
			'flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
			inset && 'pl-8',
			className,
		)}
		{...props}
	>
		{children}
		<ChevronRight className="ml-auto" />
	</DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

/** Props di {@link DropdownMenuSubContent}: nessuna prop custom. */
export interface DropdownMenuSubContentProps extends React.ComponentPropsWithoutRef<
	typeof DropdownMenuPrimitive.SubContent
> {}

/** Pannello del sottomenu, si apre accanto a {@link DropdownMenuSubTrigger}. */
const DropdownMenuSubContent = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
	DropdownMenuSubContentProps
>(({ className, ...props }, ref) => (
	<DropdownMenuPrimitive.SubContent
		ref={ref}
		className={cn(
			'z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]',
			className,
		)}
		{...props}
	/>
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

/** Props di {@link DropdownMenuContent}: eredita gli attributi standard di Radix (`sideOffset`, `align`, ecc.). */
export interface DropdownMenuContentProps extends React.ComponentPropsWithoutRef<
	typeof DropdownMenuPrimitive.Content
> {}

/** Pannello del menu, renderizzato in un portale; contiene le voci ({@link DropdownMenuItem} e simili). */
const DropdownMenuContent = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Content>,
	DropdownMenuContentProps
>(({ className, sideOffset = 4, ...props }, ref) => (
	<DropdownMenuPrimitive.Portal>
		<DropdownMenuPrimitive.Content
			ref={ref}
			sideOffset={sideOffset}
			className={cn(
				'z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]',
				className,
			)}
			{...props}
		/>
	</DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

export interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<
	typeof DropdownMenuPrimitive.Item
> {
	/** Se true, aggiunge un rientro (per allinearsi alle voci con icona/checkbox). */
	inset?: boolean;
}

/** Voce cliccabile del menu. */
const DropdownMenuItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Item>,
	DropdownMenuItemProps
>(({ className, inset, ...props }, ref) => (
	<DropdownMenuPrimitive.Item
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
			inset && 'pl-8',
			className,
		)}
		{...props}
	/>
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

/** Props di {@link DropdownMenuCheckboxItem}: eredita gli attributi standard di Radix (`checked`, `onCheckedChange`, ecc.). */
export interface DropdownMenuCheckboxItemProps extends React.ComponentPropsWithoutRef<
	typeof DropdownMenuPrimitive.CheckboxItem
> {}

/** Voce del menu con checkbox integrata (selezione multipla). */
const DropdownMenuCheckboxItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
	DropdownMenuCheckboxItemProps
>(({ className, children, checked, ...props }, ref) => (
	<DropdownMenuPrimitive.CheckboxItem
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			className,
		)}
		checked={checked}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<Check className="h-4 w-4" />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

/** Props di {@link DropdownMenuRadioItem}: eredita gli attributi standard di Radix (`value`, ecc.). */
export interface DropdownMenuRadioItemProps extends React.ComponentPropsWithoutRef<
	typeof DropdownMenuPrimitive.RadioItem
> {}

/** Voce del menu a selezione singola (radio), va dentro {@link DropdownMenuRadioGroup}. */
const DropdownMenuRadioItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
	DropdownMenuRadioItemProps
>(({ className, children, ...props }, ref) => (
	<DropdownMenuPrimitive.RadioItem
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			className,
		)}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<Circle className="h-2 w-2 fill-current" />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

export interface DropdownMenuLabelProps extends React.ComponentPropsWithoutRef<
	typeof DropdownMenuPrimitive.Label
> {
	/** Se true, aggiunge un rientro (per allinearsi alle voci con icona/checkbox). */
	inset?: boolean;
}

/** Etichetta non interattiva per raggruppare voci del menu. */
const DropdownMenuLabel = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Label>,
	DropdownMenuLabelProps
>(({ className, inset, ...props }, ref) => (
	<DropdownMenuPrimitive.Label
		ref={ref}
		className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)}
		{...props}
	/>
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

/** Props di {@link DropdownMenuSeparator}: nessuna prop custom. */
export interface DropdownMenuSeparatorProps extends React.ComponentPropsWithoutRef<
	typeof DropdownMenuPrimitive.Separator
> {}

/** Linea divisoria sottile tra gruppi di voci del menu. */
const DropdownMenuSeparator = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
	DropdownMenuSeparatorProps
>(({ className, ...props }, ref) => (
	<DropdownMenuPrimitive.Separator
		ref={ref}
		className={cn('-mx-1 my-1 h-px bg-muted', className)}
		{...props}
	/>
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

/** Props di {@link DropdownMenuShortcut}: nessuna prop custom, solo gli attributi HTML standard di uno span. */
export interface DropdownMenuShortcutProps extends React.HTMLAttributes<HTMLSpanElement> {}

/** Testo della scorciatoia da tastiera, allineato a destra di una {@link DropdownMenuItem}. */
const DropdownMenuShortcut = ({ className, ...props }: DropdownMenuShortcutProps) => {
	return (
		<span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />
	);
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuCheckboxItem,
	DropdownMenuRadioItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuGroup,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuRadioGroup,
};
